import { portalFetch } from "@/lib/portalApi";

export type AssetType = "logo" | "favicon";

export interface ThemeBrandingConfig {
  // S3 keys for stored assets (persistent)
  logo_s3_key?: string;
  favicon_s3_key?: string;
  logo_variants?: { light_bg?: string; dark_bg?: string } | null;
  // Presigned URLs (populated on-demand)
  logo_url?: string;
  favicon_url?: string;
  // Portal branding
  portal_name?: string;
}

export interface AssetUploadResponse {
  success: boolean;
  asset_type: string;
  s3_key: string;
  file_size: number;
  content_type: string;
  message: string;
}

export interface AssetUrlResponse {
  asset_type: string;
  url: string;
  s3_key: string;
  expires_in: number;
}

export interface ThemeColorMode {
  background: string;
  foreground: string;
  card: string;
  "card-foreground": string;
  popover: string;
  "popover-foreground": string;
  primary: string;
  "primary-foreground": string;
  secondary: string;
  "secondary-foreground": string;
  muted: string;
  "muted-foreground": string;
  accent: string;
  "accent-foreground": string;
  destructive: string;
  "destructive-foreground": string;
  // Semantic status colors (optional - defaults used if not provided)
  success?: string;
  "success-foreground"?: string;
  warning?: string;
  "warning-foreground"?: string;
  border: string;
  input: string;
  ring: string;
  // Chart palette — required by the control plane's theme schema
  "chart-1": string;
  "chart-2": string;
  "chart-3": string;
  "chart-4": string;
  "chart-5": string;
  "sidebar-background": string;
  "sidebar-foreground": string;
  "sidebar-primary": string;
  "sidebar-primary-foreground": string;
  "sidebar-accent": string;
  "sidebar-accent-foreground": string;
  "sidebar-border": string;
  "sidebar-ring": string;
}

export interface ThemeConfig {
  version: string;
  format: "hsl";
  light: ThemeColorMode;
  dark: ThemeColorMode;
  radius: string;
  branding?: ThemeBrandingConfig;
}

export interface OrganisationThemeResponse {
  organisation_id: string;
  has_custom_theme: boolean;
  theme_config: ThemeConfig | null;
}

// Default theme colors (HSL format)
export const defaultLightColors: ThemeColorMode = {
  background: "0 0% 100%",
  foreground: "222.2 84% 4.9%",
  card: "0 0% 100%",
  "card-foreground": "222.2 84% 4.9%",
  popover: "0 0% 100%",
  "popover-foreground": "222.2 84% 4.9%",
  primary: "222.2 47.4% 11.2%",
  "primary-foreground": "210 40% 98%",
  secondary: "210 40% 96.1%",
  "secondary-foreground": "222.2 47.4% 11.2%",
  muted: "210 40% 96.1%",
  "muted-foreground": "215.4 16.3% 46.9%",
  accent: "210 40% 96.1%",
  "accent-foreground": "222.2 47.4% 11.2%",
  destructive: "0 84.2% 60.2%",
  "destructive-foreground": "210 40% 98%",
  border: "214.3 31.8% 91.4%",
  input: "214.3 31.8% 91.4%",
  ring: "222.2 84% 4.9%",
  // Same values as --chart-1..5 in src/index.css (:root)
  "chart-1": "201.6 100% 34.9%",
  "chart-2": "41.5 100% 45.1%",
  "chart-3": "163.7 100% 31%",
  "chart-4": "26.5 100% 41.8%",
  "chart-5": "326.7 44.9% 63.7%",
  "sidebar-background": "0 0% 98%",
  "sidebar-foreground": "240 5.3% 26.1%",
  "sidebar-primary": "240 5.9% 10%",
  "sidebar-primary-foreground": "0 0% 98%",
  "sidebar-accent": "240 4.8% 95.9%",
  "sidebar-accent-foreground": "240 5.9% 10%",
  "sidebar-border": "220 13% 91%",
  "sidebar-ring": "217.2 91.2% 59.8%",
};

export const defaultDarkColors: ThemeColorMode = {
  background: "222.2 84% 4.9%",
  foreground: "210 40% 98%",
  card: "222.2 84% 4.9%",
  "card-foreground": "210 40% 98%",
  popover: "222.2 84% 4.9%",
  "popover-foreground": "210 40% 98%",
  primary: "210 40% 98%",
  "primary-foreground": "222.2 47.4% 11.2%",
  secondary: "217.2 32.6% 17.5%",
  "secondary-foreground": "210 40% 98%",
  muted: "217.2 32.6% 17.5%",
  "muted-foreground": "215 20.2% 65.1%",
  accent: "217.2 32.6% 17.5%",
  "accent-foreground": "210 40% 98%",
  destructive: "0 62.8% 30.6%",
  "destructive-foreground": "210 40% 98%",
  border: "217.2 32.6% 17.5%",
  input: "217.2 32.6% 17.5%",
  ring: "212.7 26.8% 83.9%",
  // Same values as --chart-1..5 in src/index.css (.dark)
  "chart-1": "201.6 100% 34.9%",
  "chart-2": "41.4 100% 36.1%",
  "chart-3": "163.7 100% 31%",
  "chart-4": "26.5 100% 41.8%",
  "chart-5": "326.6 45.1% 50%",
  "sidebar-background": "240 5.9% 10%",
  "sidebar-foreground": "240 4.8% 95.9%",
  "sidebar-primary": "224.3 76.3% 48%",
  "sidebar-primary-foreground": "0 0% 100%",
  "sidebar-accent": "240 3.7% 15.9%",
  "sidebar-accent-foreground": "240 4.8% 95.9%",
  "sidebar-border": "240 3.7% 15.9%",
  "sidebar-ring": "217.2 91.2% 59.8%",
};

export interface PlatformThemeResponse {
  has_custom_theme: boolean;
  theme_config: ThemeConfig | null;
}

export const themeApi = {
  // Get platform default theme
  getPlatformTheme: (token: string) =>
    portalFetch<PlatformThemeResponse>("/api/v1/platform/theme", { token }),

  // Update platform default theme
  updatePlatformTheme: (token: string, themeConfig: ThemeConfig) =>
    portalFetch<PlatformThemeResponse>("/api/v1/platform/theme", {
      token,
      method: "PUT",
      body: { theme_config: themeConfig },
    }),

  // Reset platform theme to system defaults
  resetPlatformTheme: (token: string) =>
    portalFetch<{ message: string }>("/api/v1/platform/theme", {
      token,
      method: "DELETE",
    }),

  // Upload platform theme asset
  uploadPlatformAsset: (
    token: string,
    assetType: AssetType,
    file: File,
  ): Promise<AssetUploadResponse> => {
    const formData = new FormData();
    formData.append("file", file);

    return portalFetch<AssetUploadResponse>(
      `/api/v1/platform/theme/assets/${assetType}`,
      { token, method: "POST", body: formData },
    );
  },

  // Get platform asset URL
  getPlatformAssetUrl: (
    token: string,
    assetType: AssetType,
    expiresIn?: number,
  ) => {
    const params = expiresIn ? `?expires_in=${expiresIn}` : "";
    return portalFetch<AssetUrlResponse>(
      `/api/v1/platform/theme/assets/${assetType}${params}`,
      { token },
    );
  },

  // Delete platform asset
  deletePlatformAsset: (token: string, assetType: AssetType) =>
    portalFetch<{ message: string }>(
      `/api/v1/platform/theme/assets/${assetType}`,
      { token, method: "DELETE" },
    ),

  // Get organisation theme
  getTheme: (token: string, organisationId: string) =>
    portalFetch<OrganisationThemeResponse>(
      `/api/v1/organisations/${organisationId}/theme`,
      { token },
    ),

  // Update organisation theme
  updateTheme: (
    token: string,
    organisationId: string,
    themeConfig: ThemeConfig,
  ) =>
    portalFetch<OrganisationThemeResponse>(
      `/api/v1/organisations/${organisationId}/theme`,
      {
        token,
        method: "PUT",
        body: { theme_config: themeConfig },
      },
    ),

  // Reset organisation theme to default
  resetTheme: (token: string, organisationId: string) =>
    portalFetch<{ message: string; organisation_id: string }>(
      `/api/v1/organisations/${organisationId}/theme`,
      {
        token,
        method: "DELETE",
      },
    ),

  // Upload a theme asset (logo or favicon)
  uploadAsset: (
    token: string,
    organisationId: string,
    assetType: AssetType,
    file: File,
    variant: "default" | "light_bg" | "dark_bg" = "default",
  ): Promise<AssetUploadResponse> => {
    const formData = new FormData();
    formData.append("file", file);

    const variantParam = variant !== "default" ? `?variant=${variant}` : "";
    return portalFetch<AssetUploadResponse>(
      `/api/v1/organisations/${organisationId}/theme/assets/${assetType}${variantParam}`,
      { token, method: "POST", body: formData },
    );
  },

  // Get presigned URL for a theme asset
  getAssetUrl: (
    token: string,
    organisationId: string,
    assetType: AssetType,
    variant: "default" | "light_bg" | "dark_bg" = "default",
    expiresIn?: number,
  ) => {
    const params = new URLSearchParams();
    if (variant !== "default") params.set("variant", variant);
    if (expiresIn) params.set("expires_in", String(expiresIn));
    const queryString = params.toString() ? `?${params.toString()}` : "";
    return portalFetch<AssetUrlResponse>(
      `/api/v1/organisations/${organisationId}/theme/assets/${assetType}${queryString}`,
      { token },
    );
  },

  // Delete a theme asset
  deleteAsset: (
    token: string,
    organisationId: string,
    assetType: AssetType,
    variant: "default" | "light_bg" | "dark_bg" = "default",
  ) => {
    const variantParam = variant !== "default" ? `?variant=${variant}` : "";
    return portalFetch<{
      message: string;
      organisation_id: string;
      asset_type: string;
    }>(`/api/v1/organisations/${organisationId}/theme/assets/${assetType}${variantParam}`, {
      token,
      method: "DELETE",
    });
  },
};
